import { requestSpeech } from '../api/chatApi';
import { trackChatUxEvent } from './eventTracker';
import {
  SPEECH_TOTAL_MAX,
  settledSpeechMarkdown,
  splitSpeechSegments,
  splitSpeechUnits,
  takeSpeechSegment,
  toSpeechText,
} from './speechText';

// 화면 전체에서 답변 하나만 읽는다. 상태는 subscribeSpeech로 구독한다.
//   - 음성으로 물은 턴은 답변이 나오는 동안 끝난 문장부터 읽는다(startStreamingSpeech + feedStreamingSpeech)
//   - AI 음성: 서버 mp3 스트림을 MediaSource로 받는 즉시 재생(속도를 바꿔도 목소리 높이 유지)
//   - MediaSource가 없으면 토막을 다 받은 뒤 차례로 재생(다음 토막은 미리 받아 둠)
//   - AI 음성이 실패하면(하루 상한 등) 기기 음성(speechSynthesis)으로 남은 부분을 읽는다

export const SPEECH_RATES = [1, 1.25, 1.5];
const RATE_STORAGE_KEY = 'wq_voice_rate';

const loadRate = () => {
  try {
    const saved = Number(window.localStorage.getItem(RATE_STORAGE_KEY));
    return SPEECH_RATES.includes(saved) ? saved : 1;
  } catch {
    return 1;
  }
};

const IDLE = { key: null, status: 'idle', engine: null, notice: '', noticeKey: null };
let state = { ...IDLE, rate: typeof window === 'undefined' ? 1 : loadRate() };
const listeners = new Set();
let session = null;
let sessionSeq = 0;
let sharedAudio = null;

const emit = (patch) => {
  state = { ...state, ...patch };
  listeners.forEach((listener) => listener(state));
};

export const getSpeechState = () => state;

let speechCategory = 'general';
export const setSpeechCategory = (category) => {
  speechCategory = category || 'general';
};

// 답변이 저장되면 메시지 id가 임시 id에서 문서 id로 바뀐다. 두 id를 같은 답변으로 본다.
const ALIAS_LIMIT = 20;
const keyAliases = new Map();
const rootKey = (key) => keyAliases.get(key) || key;

export const linkSpeechKey = (key, alias) => {
  if (!key || !alias || key === alias) return;
  keyAliases.set(alias, rootKey(key));
  if (keyAliases.size > ALIAS_LIMIT) keyAliases.delete(keyAliases.keys().next().value);
};

export const isSameSpeechKey = (a, b) => a != null && b != null && rootKey(a) === rootKey(b);

export const subscribeSpeech = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

const getAudio = () => {
  if (!sharedAudio) {
    sharedAudio = new Audio();
    sharedAudio.preload = 'auto';
  }
  return sharedAudio;
};

const getMediaSourceCtor = () => {
  if (typeof window === 'undefined') return null;
  const Ctor = window.ManagedMediaSource || window.MediaSource;
  if (!Ctor || typeof Ctor.isTypeSupported !== 'function') return null;
  return Ctor.isTypeSupported('audio/mpeg') ? Ctor : null;
};

const canUseDeviceVoice = () => (
  typeof window !== 'undefined'
  && 'speechSynthesis' in window
  && typeof window.SpeechSynthesisUtterance === 'function'
);

// iOS는 사용자 동작 안에서 한 번 play()한 오디오 요소만 이후 재생을 허락한다
const silentWavUrl = (() => {
  let url = null;
  return () => {
    if (url) return url;
    const samples = 240;
    const buffer = new ArrayBuffer(44 + samples * 2);
    const view = new DataView(buffer);
    const writeText = (offset, text) => [...text].forEach((ch, i) => view.setUint8(offset + i, ch.charCodeAt(0)));
    writeText(0, 'RIFF');
    view.setUint32(4, 36 + samples * 2, true);
    writeText(8, 'WAVE');
    writeText(12, 'fmt ');
    view.setUint32(16, 16, true);
    view.setUint16(20, 1, true);
    view.setUint16(22, 1, true);
    view.setUint32(24, 24000, true);
    view.setUint32(28, 48000, true);
    view.setUint16(32, 2, true);
    view.setUint16(34, 16, true);
    writeText(36, 'data');
    view.setUint32(40, samples * 2, true);
    url = URL.createObjectURL(new Blob([buffer], { type: 'audio/wav' }));
    return url;
  };
})();

const isStale = (s) => session !== s;

const reportEnd = (s, outcome, errorCode) => {
  if (s.reported) return;
  s.reported = true;
  trackChatUxEvent('voice_speak', {
    auto: s.auto,
    category: s.category,
    engine: s.engine,
    errorCode,
    firstAudioMs: s.firstAudioAt ? s.firstAudioAt - s.startedAt : 0,
    outcome,
    segmentCount: s.segmentCount,
    voiceChars: s.chars,
  });
};

const cleanupSession = (s) => {
  s.controller.abort();
  s.wake?.();
  s.wake = null;
  s.urls.forEach((url) => URL.revokeObjectURL(url));
  s.urls = [];
  s.detach?.();
};

export const stopSpeech = () => {
  const s = session;
  session = null;
  if (s) {
    reportEnd(s, 'stopped');
    cleanupSession(s);
  }
  if (sharedAudio) {
    sharedAudio.pause();
    sharedAudio.removeAttribute('src');
    try {
      sharedAudio.load();
    } catch {
      // jsdom 등 load 미지원 환경
    }
  }
  if (canUseDeviceVoice()) window.speechSynthesis.cancel();
  if (state.status !== 'idle') emit({ ...IDLE });
};

const finish = (s) => {
  if (isStale(s)) return;
  session = null;
  reportEnd(s, 'completed', s.fallbackCode);
  cleanupSession(s);
  emit({ ...IDLE });
};

const handlePlayRejected = (s, error) => {
  if (isStale(s) || error?.name !== 'NotAllowedError') return false;
  session = null;
  reportEnd(s, 'blocked', 'NotAllowedError');
  cleanupSession(s);
  if (sharedAudio) sharedAudio.pause();
  emit({ ...IDLE, notice: '듣기 버튼을 누르면 읽어 드려요.', noticeKey: s.key });
  return true;
};

// 자동 읽기용: 사용자 동작(전송 클릭) 안에서 불러 두면 답변이 끝난 뒤 재생이 막히지 않는다(iOS)
export const primeSpeechAudio = () => {
  if (typeof window === 'undefined') return;
  stopSpeech();
  const audio = getAudio();
  audio.src = silentWavUrl();
  audio.play().catch(() => {});
};

const attachAudioEvents = (s, audio) => {
  const onPlaying = () => {
    if (isStale(s) || audio.src === silentWavUrl()) return;
    if (!s.firstAudioAt) {
      s.firstAudioAt = Date.now();
      s.onFirstAudio?.(s.firstAudioAt - s.startedAt);
    }
    emit({ status: 'playing' });
  };
  audio.addEventListener('playing', onPlaying);
  s.detach = () => audio.removeEventListener('playing', onPlaying);
};

const waitForEnded = (audio, s) => new Promise((resolve) => {
  if (audio.ended) {
    resolve();
    return;
  }
  const done = () => {
    audio.removeEventListener('ended', done);
    s.controller.signal.removeEventListener('abort', done);
    resolve();
  };
  audio.addEventListener('ended', done);
  s.controller.signal.addEventListener('abort', done);
});

const appendChunk = (sourceBuffer, chunk) => new Promise((resolve, reject) => {
  const onEnd = () => {
    sourceBuffer.removeEventListener('updateend', onEnd);
    sourceBuffer.removeEventListener('error', onError);
    resolve();
  };
  const onError = () => {
    sourceBuffer.removeEventListener('updateend', onEnd);
    sourceBuffer.removeEventListener('error', onError);
    reject(new Error('append_failed'));
  };
  sourceBuffer.addEventListener('updateend', onEnd);
  sourceBuffer.addEventListener('error', onError);
  sourceBuffer.appendBuffer(chunk);
});

// ---- 읽을 글 대기열: 「듣기」는 처음부터 전부 넣고, 음성 질문의 답변은 글이 나오는 대로 문장 단위로 넣는다
const enqueueUnits = (s, units) => {
  if (units.length) s.pending.push(...units);
  s.wake?.();
  s.wake = null;
};

const endQueue = (s) => {
  s.ended = true;
  s.wake?.();
  s.wake = null;
};

// 앞 토막을 읽는 동안 쌓인 문장을 한 번에 묶어 다음 요청으로 보낸다. 더 읽을 게 없으면 null.
const takeSegment = async (s) => {
  while (!isStale(s)) {
    if (s.pending.length) {
      const { segment, rest } = takeSpeechSegment(s.pending);
      s.pending = rest;
      s.segmentCount += 1;
      s.chars += segment.length;
      return segment;
    }
    if (s.ended) return null;
    await new Promise((resolve) => { s.wake = resolve; });
  }
  return null;
};

async function playStreaming(s, audio, MediaSourceCtor) {
  const mediaSource = new MediaSourceCtor();
  s.mediaSource = mediaSource;
  const url = URL.createObjectURL(mediaSource);
  s.urls.push(url);
  if ('disableRemotePlayback' in audio) audio.disableRemotePlayback = true;
  const opened = new Promise((resolve) => mediaSource.addEventListener('sourceopen', resolve, { once: true }));
  audio.src = url;
  audio.playbackRate = state.rate;
  audio.play().catch((error) => handlePlayRejected(s, error));
  await opened;
  if (isStale(s)) return;
  const sourceBuffer = mediaSource.addSourceBuffer('audio/mpeg');
  try {
    sourceBuffer.mode = 'sequence';
  } catch {
    // audio/mpeg는 기본이 sequence
  }

  for (;;) {
    const segment = await takeSegment(s);
    if (isStale(s)) return;
    if (segment === null) break;
    s.current = segment;
    const response = await requestSpeech(segment, { signal: s.controller.signal });
    const reader = response.body.getReader();
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      if (isStale(s)) {
        reader.cancel().catch(() => {});
        return;
      }
      await appendChunk(sourceBuffer, value);
      s.spokeAny = true;
    }
    s.current = null;
  }
  if (!isStale(s) && mediaSource.readyState === 'open') mediaSource.endOfStream();
  await waitForEnded(audio, s);
}

async function playSequential(s, audio) {
  const nextItem = async () => {
    const text = await takeSegment(s);
    if (text === null || isStale(s)) return null;
    const blob = requestSpeech(text, { signal: s.controller.signal }).then((res) => res.blob());
    blob.catch(() => {});
    return { text, blob };
  };
  let item = await nextItem();
  while (item) {
    s.current = item.text;
    const blob = await item.blob;
    if (isStale(s)) return;
    const following = nextItem();
    following.catch(() => {});
    const url = URL.createObjectURL(blob);
    s.urls.push(url);
    audio.src = url;
    audio.playbackRate = state.rate;
    await audio.play();
    s.spokeAny = true;
    await waitForEnded(audio, s);
    if (isStale(s)) return;
    s.current = null;
    item = await following;
  }
}

const speakUtterance = (s, text) => new Promise((resolve) => {
  const utterance = new window.SpeechSynthesisUtterance(text);
  utterance.lang = 'ko-KR';
  utterance.rate = state.rate;
  utterance.onstart = () => {
    if (!isStale(s)) emit({ status: 'playing' });
  };
  utterance.onend = () => resolve(true);
  utterance.onerror = () => resolve(false);
  window.speechSynthesis.speak(utterance);
});

async function speakWithDevice(s, first) {
  window.speechSynthesis.cancel();
  let text = first || await takeSegment(s);
  while (text && !isStale(s)) {
    if (!(await speakUtterance(s, text))) return;
    text = await takeSegment(s);
  }
}

const FAILURE_NOTICES = {
  daily_limit: '오늘 AI 음성을 다 써서 기기 음성으로 읽어요.',
  login_required: '로그인하면 AI 음성으로 들을 수 있어요.',
};

async function fallbackToDevice(s, error) {
  if (isStale(s)) return;
  const first = s.current;
  if (s.spokeAny && sharedAudio && !sharedAudio.paused) {
    // 이미 받아 둔 앞부분은 끝까지 들려준 뒤 실패한 토막부터 기기 음성으로 잇는다
    let canDrain = true;
    if (s.mediaSource?.readyState === 'open') {
      try {
        s.mediaSource.endOfStream();
      } catch {
        canDrain = false;
      }
    }
    if (canDrain) await waitForEnded(sharedAudio, s);
    if (isStale(s)) return;
  }
  const nothingLeft = !first && !s.pending.length && s.ended;
  if (!canUseDeviceVoice() || nothingLeft) {
    emit({ ...IDLE, notice: error?.message || 'AI 음성을 만들지 못했어요.', noticeKey: s.key });
    session = null;
    reportEnd(s, 'failed', error?.code || 'engine_error');
    cleanupSession(s);
    return;
  }
  if (sharedAudio) sharedAudio.pause();
  s.engine = 'device';
  s.fallbackCode = error?.code || 'engine_error';
  emit({
    engine: 'device',
    notice: FAILURE_NOTICES[error?.code] || 'AI 음성이 잠시 안 돼서 기기 음성으로 읽어요.',
    noticeKey: s.key,
  });
  await speakWithDevice(s, first);
  finish(s);
}

const TRUNCATED_NOTICE = '답변이 길어 앞부분만 읽어요.';

const startSession = (key, { auto, onFirstAudio, streaming }) => {
  stopSpeech();
  const audio = getAudio();
  sessionSeq += 1;
  const s = {
    id: sessionSeq,
    key,
    auto,
    category: speechCategory,
    engine: 'ai',
    chars: 0,
    segmentCount: 0,
    pending: [],
    ended: false,
    wake: null,
    current: null,
    streaming,
    settledLength: 0,
    queuedChars: 0,
    controller: new AbortController(),
    urls: [],
    spokeAny: false,
    startedAt: Date.now(),
    firstAudioAt: 0,
    onFirstAudio,
    reported: false,
  };
  session = s;
  attachAudioEvents(s, audio);
  emit({ key, status: 'loading', engine: 'ai', notice: '', noticeKey: null });

  const MediaSourceCtor = getMediaSourceCtor();
  if (!MediaSourceCtor) {
    audio.src = silentWavUrl();
    audio.play().catch(() => {});
  }
  const run = MediaSourceCtor ? playStreaming(s, audio, MediaSourceCtor) : playSequential(s, audio);
  run
    .then(() => finish(s))
    .catch((error) => {
      if (isStale(s) || error?.name === 'AbortError') return;
      if (handlePlayRejected(s, error)) return;
      fallbackToDevice(s, error);
    });
  return s;
};

export const speakMessage = (key, markdown, { auto = false, onFirstAudio = null } = {}) => {
  const { segments, truncated } = splitSpeechSegments(toSpeechText(markdown));
  if (segments.length === 0) {
    stopSpeech();
    return false;
  }
  const s = startSession(key, { auto, onFirstAudio, streaming: false });
  enqueueUnits(s, segments.map((text) => ({ text, separator: '\n' })));
  endQueue(s);
  if (truncated) emit({ notice: TRUNCATED_NOTICE, noticeKey: key });
  return true;
};

// 음성으로 물은 턴: 답변 글이 나오는 동안 끝난 문장부터 바로 읽는다.
export const startStreamingSpeech = (key, { auto = true, onFirstAudio = null } = {}) => {
  startSession(key, { auto, onFirstAudio, streaming: true });
};

// 지금까지 나온 답변 전체를 넘긴다. 사용자가 멈췄거나 다른 답변을 읽는 중이면 false.
export const feedStreamingSpeech = (key, markdown, { final = false } = {}) => {
  const s = session;
  if (!s || !isSameSpeechKey(s.key, key) || !s.streaming || s.ended) return false;
  const settled = settledSpeechMarkdown(markdown, { final });
  if (settled.length > s.settledLength) {
    const delta = settled.slice(s.settledLength);
    const startsLine = s.settledLength === 0 || settled[s.settledLength - 1] === '\n';
    s.settledLength = settled.length;
    const kept = [];
    let truncated = false;
    for (const unit of splitSpeechUnits(toSpeechText(delta), { startsLine })) {
      if (s.queuedChars + unit.text.length > SPEECH_TOTAL_MAX) {
        truncated = true;
        break;
      }
      s.queuedChars += unit.text.length;
      kept.push(unit);
    }
    enqueueUnits(s, kept);
    if (truncated) {
      emit({ notice: TRUNCATED_NOTICE, noticeKey: key });
      endQueue(s);
      return true;
    }
  }
  if (final) endQueue(s);
  return true;
};

export const setSpeechRate = (rate) => {
  if (!SPEECH_RATES.includes(rate)) return;
  try {
    window.localStorage.setItem(RATE_STORAGE_KEY, String(rate));
  } catch {
    // 저장 실패는 무시 (사생활 보호 모드 등)
  }
  if (sharedAudio) {
    sharedAudio.playbackRate = rate;
    sharedAudio.defaultPlaybackRate = rate;
  }
  emit({ rate });
};

export const clearSpeechNotice = () => {
  if (state.notice) emit({ notice: '', noticeKey: null });
};

export const __resetSpeechPlayerForTests = () => {
  stopSpeech();
  sharedAudio = null;
  listeners.clear();
  keyAliases.clear();
  speechCategory = 'general';
  state = { ...IDLE, rate: loadRate() };
};
